/**
 * CharacterCard Component
 *
 * Card component for displaying and selecting characters in the wizard.
 * Supports multi-select with visual feedback.
 */

import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { faSpinner } from '@fortawesome/pro-light-svg-icons/faSpinner'

import { Icon } from '@/components/Icon/Icon'
import { CharacterList } from '@/shared/api/models'
import { pxToRem } from '@/shared/styles/themes'
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  styled,
  Typography,
} from '@mui/material'

interface CharacterCardProps {
  character: CharacterList
  isSelected: boolean
  onClick: () => void
  disabled?: boolean
}

export const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  isSelected,
  onClick,
  disabled = false,
}) => {
  const imageUrl = character.character_image_url || character.reference_photo_url
  const isGenerating =
    character.avatar_generation_status === 'pending' ||
    character.avatar_generation_status === 'generating'

  return (
    <StyledCharacterCard $selected={isSelected} $disabled={disabled}>
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{ height: '100%' }}
      >
        <StyledImageContainer>
          <StyledAvatar src={imageUrl} alt={character.name}>
            {character.name.charAt(0).toUpperCase()}
          </StyledAvatar>
          {isSelected && (
            <StyledCheckmark>
              <Icon icon={faCheck} sx={{ fontSize: pxToRem(14) }} />
            </StyledCheckmark>
          )}
          {isGenerating && (
            <StyledGeneratingBadge>
              <Icon icon={faSpinner} spin sx={{ fontSize: pxToRem(12) }} />
            </StyledGeneratingBadge>
          )}
        </StyledImageContainer>
        <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {character.name}
          </Typography>
          {character.usage_count > 0 && (
            <Chip
              label={`${character.usage_count} stor${character.usage_count === 1 ? 'y' : 'ies'}`}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
            />
          )}
          {isGenerating && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              Generating avatar...
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </StyledCharacterCard>
  )
}

interface AddNewCharacterCardProps {
  onClick: () => void
}

export const AddNewCharacterCard: React.FC<AddNewCharacterCardProps> = ({ onClick }) => {
  return (
    <StyledAddNewCard>
      <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 3 }}>
        <StyledAddIcon>
          <Icon icon={faPlus} sx={{ fontSize: pxToRem(24) }} />
        </StyledAddIcon>
        <Typography variant="body2" fontWeight="medium" sx={{ mt: 1 }}>
          Add New Character
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upload a photo
        </Typography>
      </CardActionArea>
    </StyledAddNewCard>
  )
}

const StyledCharacterCard = styled(Card, {
  shouldForwardProp: prop => prop !== '$selected' && prop !== '$disabled',
})<{ $selected?: boolean; $disabled?: boolean }>`
  height: 100%;
  border: 3px solid ${({ $selected, theme }) =>
    $selected ? theme.palette.secondary.main : 'transparent'};
  transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  border-radius: ${pxToRem(12)};
  overflow: hidden;

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-4px)')};
    box-shadow: ${({ $disabled, theme }) =>
    $disabled ? 'none' : theme.shadows[4]};
  }
`

const StyledAddNewCard = styled(Card)`
  height: 100%;
  min-height: ${pxToRem(180)};
  border: 2px dashed ${({ theme }) => theme.palette.divider};
  background: transparent;
  border-radius: ${pxToRem(12)};
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.palette.secondary.main};
    background-color: ${({ theme }) => theme.palette.custom.accentyellow};
  }
`

const StyledImageContainer = styled(Box)`
  position: relative;
  padding-top: 100%;
  background-color: ${({ theme }) => theme.palette.background.default};
`

const StyledAvatar = styled(Avatar)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 75%;
  height: 75%;
  font-size: ${pxToRem(36)};
  background-color: ${({ theme }) => theme.palette.primary.main};
`

const StyledCheckmark = styled(Box)`
  position: absolute;
  top: ${pxToRem(8)};
  right: ${pxToRem(8)};
  width: ${pxToRem(28)};
  height: ${pxToRem(28)};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.secondary.main};
  color: ${({ theme }) => theme.palette.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`

const StyledGeneratingBadge = styled(Box)`
  position: absolute;
  bottom: ${pxToRem(8)};
  right: ${pxToRem(8)};
  background-color: ${({ theme }) => theme.palette.warning.main};
  color: white;
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledAddIcon = styled(Box)`
  width: ${pxToRem(56)};
  height: ${pxToRem(56)};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.custom.lightpurple};
  color: ${({ theme }) => theme.palette.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
`

export default CharacterCard
